/**
 * CurrencyInput renders an input field that format it's value according to currency formatting rules
 * onFocus: renders given value in unformatted manner: "9999,99"
 * onBlur: formats the given input: "9 999,99 €"
 */
import React, { useEffect, useState } from 'react';
import { bool, func, number, object, oneOfType, shape, string } from 'prop-types';
import { Field } from 'react-final-form';
import classNames from 'classnames';
import Decimal from 'decimal.js';

import { intlShape, injectIntl } from '../../util/reactIntl';
import { types as sdkTypes } from '../../util/sdkLoader';
import {
  isSafeNumber,
  unitDivisor,
  convertUnitToSubUnit,
  ensureDotSeparator,
  ensureSeparator,
  truncateToSubUnitPrecision,
} from '../../util/currency';
import { propTypes } from '../../util/types';

import css from './FieldCurrencyInput.module.css';

const { Money } = sdkTypes;

const allowedInputProps = allProps => {
  // Strip away props that are not passed to input element (or are overwritten)

  const { currencyConfig, defaultValue, intl, input, meta, ...inputProps } = allProps;
  return inputProps;
};

// Convert unformatted value (e.g. 10,00) to Money (or null)
const getPrice = (unformattedValue, currencyConfig) => {
  const isEmptyString = unformattedValue === '';
  try {
    return isEmptyString
      ? null
      : new Money(
          convertUnitToSubUnit(unformattedValue, unitDivisor(currencyConfig.currency)),
          currencyConfig.currency
        );
  } catch {
    return null;
  }
};

function CurrencyInputComponent(props) {
  const { currencyConfig, defaultValue = null, input, intl } = props;
  const initialValueIsMoney = input.value instanceof Money;
  const [value, setValue] = useState(
    initialValueIsMoney && input.value.currency === currencyConfig.currency
      ? input.value
      : defaultValue
  );

  useEffect(() => {
    const newValue =
      initialValueIsMoney && input.value.currency === currencyConfig.currency
        ? input.value
        : defaultValue;
    setValue(newValue);
  }, [currencyConfig, defaultValue, input.value, initialValueIsMoney]);

  const hasInitialValue = typeof value === 'number' && !isNaN(value);

  // We need to handle number format - some locales use dots and some commas as decimal separator
  // TODO Figure out if this could be digged from React-Intl directly somehow
  const testSubUnitFormat = intl.formatNumber('1.1', currencyConfig);
  const usesComma = testSubUnitFormat.indexOf(',') >= 0;

  const formattedValue = hasInitialValue
    ? intl.formatNumber(ensureDotSeparator(value.toString()), currencyConfig)
    : '';
  const unformattedValue = hasInitialValue
    ? truncateToSubUnitPrecision(
        ensureSeparator(value.toString(), usesComma),
        unitDivisor(currencyConfig.currency),
        usesComma
      )
    : '';

  const onInputChange = event => {
    event.preventDefault();
    event.stopPropagation();
    // Update value strings on state
    const { unformattedValue } = updateValues(event);
    // Notify parent component about current price change
    const price = getPrice(ensureDotSeparator(unformattedValue), currencyConfig);
    input.onChange(price);
  };

  const onInputBlur = event => {
    event.preventDefault();
    event.stopPropagation();
    const {
      currencyConfig,
      input: { onBlur },
    } = props;
    setValue(formattedValue);
    if (onBlur) {
      // If parent component has provided onBlur function, call it with current price.
      const price = getPrice(ensureDotSeparator(unformattedValue), currencyConfig);
      onBlur(price);
    }
  };

  const onInputFocus = event => {
    event.preventDefault();
    event.stopPropagation();
    const {
      currencyConfig,
      input: { onFocus },
    } = props;
    setValue(unformattedValue);
    if (onFocus) {
      // If parent component has provided onFocus function, call it with current price.
      const price = getPrice(ensureDotSeparator(unformattedValue), currencyConfig);
      onFocus(price);
    }
  };

  const updateValues = event => {
    try {
      const targetValue = event.target.value.trim();
      const isEmptyString = targetValue === '';
      const valueOrZero = isEmptyString ? '0' : targetValue;

      const targetDecimalValue = isEmptyString
        ? null
        : new Decimal(ensureDotSeparator(targetValue));

      const isSafeValue =
        isEmptyString || (targetDecimalValue.isPositive() && isSafeNumber(targetDecimalValue));
      if (!isSafeValue) {
        throw new Error(`Unsafe money value: ${targetValue}`);
      }

      // truncate decimals to subunit precision: 10000.999 => 10000.99
      const truncatedValueString = truncateToSubUnitPrecision(
        valueOrZero,
        unitDivisor(currencyConfig.currency),
        usesComma
      );
      const newUnformattedValue = !isEmptyString ? truncatedValueString : '';
      const newFormattedValue = !isEmptyString
        ? intl.formatNumber(ensureDotSeparator(truncatedValueString), currencyConfig)
        : '';

      setValue(newUnformattedValue);

      return {
        formattedValue: newFormattedValue,
        value: newUnformattedValue,
        unformattedValue: newUnformattedValue,
      };
    } catch (e) {
      console.warn('Not a valid value.', e);

      // If an error occurs while filling input field, use previous values
      // This ensures that string like '12.3r' doesn't end up to a state.
      return { formattedValue, unformattedValue, value };
    }
  };

  const { className, placeholder } = props;
  const placeholderText = placeholder || intl.formatNumber(defaultValue, currencyConfig);
  return (
    <input
      className={className}
      {...allowedInputProps(props)}
      value={value}
      onChange={onInputChange}
      onBlur={onInputBlur}
      onFocus={onInputFocus}
      type="text"
      placeholder={placeholderText}
    />
  );
}

CurrencyInputComponent.propTypes = {
  className: string,
  currencyConfig: propTypes.currencyConfig.isRequired,
  defaultValue: number,
  intl: intlShape.isRequired,
  input: shape({
    value: oneOfType([string, propTypes.money]),
    onBlur: func,
    onChange: func.isRequired,
    onFocus: func,
  }).isRequired,

  placeholder: string,
};

export const CurrencyInput = injectIntl(CurrencyInputComponent);

const FieldCurrencyInputComponent = props => {
  const {
      rootClassName = null,
      className = null,
      id = null,
      label = null,
      input,
      meta,
      hideErrorMessage = false,
      ...rest
  } = props;

  if (label && !id) {
    throw new Error('id required when a label is given');
  }

  const { valid, invalid, touched, error } = meta;

  // Error message and input error styles are only shown if the
  // field has been touched and the validation has failed.
  const hasError = touched && invalid && error;

  const inputClasses = classNames(css.input, {
    [css.inputSuccess]: valid,
    [css.inputError]: hasError,
  });

  const inputProps = { className: inputClasses, id, input, ...rest };
  const classes = classNames(rootClassName, className);
  return (
    <div className={classes}>
      {label ? <label htmlFor={id}>{label}</label> : null}
      <CurrencyInput {...inputProps} />
      {hideErrorMessage ? null : <ValidationError fieldMeta={meta} />}
    </div>
  );
};

FieldCurrencyInputComponent.propTypes = {
  rootClassName: string,
  className: string,

  // Label is optional, but if it is given, an id is also required so
  // the label can reference the input in the `for` attribute
  id: string,
  label: string,
  hideErrorMessage: bool,

  // Generated by final-form's Field component
  input: object.isRequired,
  meta: object.isRequired,
};

const FieldCurrencyInput = props => {
  return <Field component={FieldCurrencyInputComponent} {...props} />;
};

export default FieldCurrencyInput;
